import React, { useState, useMemo, useEffect } from 'react';
import Loading from 'components/Loading';
import MultiStepModal from 'ui/Modals/MultiStepModal';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { BudgetBys, BudgetExpenseOrIncome } from 'business/constants';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { translateBlockValue } from 'i18n';
import {
    BudgetProposalT,
    ResourceSoftwareIdField,
    BudgetLine,
    ResourceBudgetProposalAssociationT,
} from 'routes/BudgetModule/config/types';
import {
    BUDGET_LINE_ID,
    CHAPTER_FIELD,
    CHAPTER_SOFTWARE_ID,
    NATURE_FIELD,
    NATURE_SOFTWARE_ID,
    SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';
import {
    getResourceNameField,
    getResourceSoftwareIdField,
} from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import {
    getBudgetLineColumns,
    getResourceColumns,
} from 'routes/BudgetModule/components/shared/InscriptionsGrid/AddInscription/helpers/colDefinitions';
import RadioCellSelector from 'routes/BudgetModule/components/shared/InscriptionsGrid/AddInscription/RadioCellSelector';
import { styled } from '@mui/material/styles';
import {
    getMoveInscriptionStepTitles,
    getMoveInscriptionSubtitle,
} from './helpers/typographyHelpers';
import { useMutateMoveInscription } from './helpers/api/hooks/moveInscription';
import { getInitialChoice } from './helpers/dataHelpers';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useGetMoveBudgetLines } from 'routes/BudgetModule/helpers/hooks/useGetBudgetLines';
import { Box } from '@mui/material';
import ToggleWithTooltip from 'ui/ToggleButton/ToggleWithTooltip';
import { useGetResourcesInBudgetProposal } from '../../BudgetChart/BudgetPivotGrid/InscriptionsModal/hooks';

const StyTitleDetails = styled(Typography, {
    name: 'StyTitleDetails',
})({
    margin: '10px 0 30px 0',
});

const StySubtitle = styled(Typography, {
    name: 'StySubtitle',
})({
    fontWeight: '400',
    fontStyle: 'italic',
});

type Props = {
    isOpen: boolean;
    onClose: () => void;
    selectedInscription: InscriptionT;
    queryCacheKey: InscriptionQueryKey;
    inscriptionIdColumn: keyof InscriptionT;
    imputationDimensions: string[];
    budgetProposal: BudgetProposalT;
    budgetProposals: BudgetProposalT[];
    expenseOrIncome: BudgetExpenseOrIncome;
};

/**
 * Create the filters to get the resources in the budget proposal that match the selected inscription.
 */
const buildFiltersForResourcesInBudgetProposal = (
    inscription: InscriptionT,
    shouldFilter: boolean,
): QueryFilterT[] => {
    if (!shouldFilter) {
        return [];
    }

    const chapter = inscription[CHAPTER_SOFTWARE_ID];
    const nature = inscription[NATURE_SOFTWARE_ID];
    const sba = inscription[SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID];

    const filters: QueryFilterT[] = [];

    if (chapter)
        filters.push({
            field: CHAPTER_SOFTWARE_ID,
            value: chapter,
            operation: 'equal',
        });

    if (nature)
        filters.push({
            field: NATURE_SOFTWARE_ID,
            value: nature,
            operation: 'equal',
        });

    if (sba)
        filters.push({
            field: SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
            value: sba,
            operation: 'equal',
        });

    return filters;
};

function MoveInscriptionModal({
    isOpen,
    onClose: onCloseProp,
    selectedInscription,
    inscriptionIdColumn,
    imputationDimensions,
    queryCacheKey,
    budgetProposal,
    budgetProposals,
    expenseOrIncome,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const { by } = budgetProposal;
    const resourceNameField = getResourceNameField(by);
    const resourceName = translateBlockValue(resourceNameField, true);
    const resourceSoftwareIdField: ResourceSoftwareIdField =
        getResourceSoftwareIdField(by);
    const inscriptionId = selectedInscription[inscriptionIdColumn] as number; // TODO: improve typing
    const [shouldFilterPreclassification, setShouldFilterPreclassification] =
        useState(true);

    const budgetProposalFilters = useMemo(
        () =>
            buildFiltersForResourcesInBudgetProposal(
                selectedInscription,
                shouldFilterPreclassification,
            ),
        [selectedInscription, shouldFilterPreclassification],
    );

    const { data: budgetProposalResources = [], isLoading } =
        useGetResourcesInBudgetProposal(
            budgetProposal.id,
            undefined,
            undefined,
            budgetProposalFilters,
        );

    // Initial choice is made of the selected inscription resource and a budget line set to null
    // This allow the user to see which resource is initial budget line belongs too.
    const initialChoice = getInitialChoice(
        budgetProposalResources,
        resourceSoftwareIdField,
        selectedInscription,
    );

    const [selectedBudgetProposalResource, setSelectedBudgetProposalResource] =
        useState<null | ResourceBudgetProposalAssociationT>(null);

    useEffect(() => {
        setSelectedBudgetProposalResource(initialChoice.budgetProposalResource);
    }, [budgetProposalResources, initialChoice.budgetProposalResource]);

    // selectedBudgetLine is null whenever the modal is closed.
    const [selectedBudgetLine, setSelectedBudgetLine] =
        useState<null | BudgetLine>(initialChoice.budgetLine);

    const { data: budgetLines = [], isLoading: areBudgetLinesLoading } =
        useGetMoveBudgetLines({
            budgetBy:
                selectedBudgetProposalResource?.budgetProposal.by ??
                BudgetBys.GESTIONNAIRE,
            budgetName:
                selectedBudgetProposalResource?.budgetProposal.budgetName,
            investmentOperating:
                selectedBudgetProposalResource?.budgetProposal.section,
            exercice: selectedBudgetProposalResource?.budgetProposal.exercice,
            expenseOrIncome,
            resourceSoftwareId:
                selectedBudgetProposalResource?.resource?.resourceSoftwareId,
            enabled: selectedBudgetProposalResource != null,
        });

    const filteredBudgetLines = useMemo(() => {
        if (!shouldFilterPreclassification) {
            return budgetLines;
        }

        const chapter = selectedInscription[CHAPTER_FIELD];
        const nature = selectedInscription[NATURE_FIELD];

        return budgetLines.filter((budgetLine) => {
            const matchesChapter =
                !chapter || budgetLine[CHAPTER_FIELD] === chapter;
            const matchesNature =
                !nature || budgetLine[NATURE_FIELD] === nature;
            return matchesChapter && matchesNature;
        });
    }, [budgetLines, shouldFilterPreclassification, selectedInscription]);

    const moveInscriptionMutation = useMutateMoveInscription(queryCacheKey);
    const stepTitles = getMoveInscriptionStepTitles(t, resourceName);
    const subtitle = getMoveInscriptionSubtitle(
        t,
        selectedInscription,
        budgetProposals,
    );
    const columns = getResourceColumns(resourceNameField);
    const firstStep = useMemo(
        () => (
            <RadioCellSelector
                dataRows={budgetProposalResources}
                selectedRow={selectedBudgetProposalResource}
                setSelectedRow={setSelectedBudgetProposalResource}
                columns={columns}
                pagination
                paginationPageSize={10}
            />
        ),
        [budgetProposalResources, columns, selectedBudgetProposalResource],
    );

    const secondStep = useMemo(
        () => (
            <RadioCellSelector
                dataRows={filteredBudgetLines}
                selectedRow={selectedBudgetLine}
                setSelectedRow={setSelectedBudgetLine}
                pagination
                paginationPageSize={20}
                columns={getBudgetLineColumns(imputationDimensions)}
            />
        ),
        [filteredBudgetLines, imputationDimensions, selectedBudgetLine],
    );
    const steps = [
        {
            // First step is for selecting a budget proposal by resource.
            confirmAction: {
                onClick: () =>
                    setSelectedBudgetLine(
                        filteredBudgetLines.length > 0
                            ? filteredBudgetLines[0]
                            : null,
                    ),
                enabled: selectedBudgetProposalResource !== null,
            },
            stepBackAction: {
                onClick: () =>
                    setSelectedBudgetProposalResource(
                        initialChoice.budgetProposalResource,
                    ),
            },
            node: (
                <>
                    <Typography display="inline" variant="body1SemiBold">
                        {stepTitles.budgetProposalSelection.title}
                    </Typography>
                    <StyTitleDetails display="inline" variant="body1">
                        {` - ${
                            stepTitles.budgetProposalSelection.subTitle || ''
                        }`}
                    </StyTitleDetails>
                    <StySubtitle variant="body2">{subtitle}</StySubtitle>
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
                        <ToggleWithTooltip
                            title={t(
                                'moveInscriptionModal.preClassificationToggle',
                            )}
                            tooltipTitle={t(
                                'moveInscriptionModal.budgetProposalPreClassificationTitle',
                                {
                                    dimension: `${resourceName.toLowerCase()}`,
                                },
                            )}
                            isChecked={shouldFilterPreclassification}
                            onChange={(value) =>
                                setShouldFilterPreclassification(
                                    value.currentTarget.checked,
                                )
                            }
                        />
                    </Box>
                    {isLoading ? <Loading /> : firstStep}
                </>
            ),
        },
        {
            // Second step is for selecting a budget line corresponding to
            // the selected budget proposal by resource.
            confirmAction: {
                onClick: () =>
                    moveInscriptionMutation.mutate({
                        id: inscriptionId,
                        data: selectedBudgetLine
                            ? {
                                  [BUDGET_LINE_ID]:
                                      selectedBudgetLine[BUDGET_LINE_ID],
                              }
                            : {},
                    }),
                enabled: selectedBudgetLine !== null,
                label: 'common.move',
            },
            stepBackAction: {
                onClick: () => setSelectedBudgetLine(initialChoice.budgetLine),
            },
            node: (
                <>
                    <Typography variant="body1SemiBold" display="inline">
                        {stepTitles.budgetLineSelection.title}
                    </Typography>
                    <StyTitleDetails display="inline" variant="body1">
                        {` - ${stepTitles.budgetLineSelection.subTitle || ''}`}
                    </StyTitleDetails>
                    <StySubtitle variant="body2">{subtitle}</StySubtitle>
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
                        <ToggleWithTooltip
                            title={t(
                                'moveInscriptionModal.preClassificationToggle',
                            )}
                            tooltipTitle={t(
                                'moveInscriptionModal.budgetLinePreClassificationTitle',
                            )}
                            isChecked={shouldFilterPreclassification}
                            onChange={(value) =>
                                setShouldFilterPreclassification(
                                    value.currentTarget.checked,
                                )
                            }
                        />
                    </Box>
                    {areBudgetLinesLoading ? <Loading /> : secondStep}
                </>
            ),
        },
    ];

    return (
        <MultiStepModal
            isOpen={isOpen}
            onClose={onCloseProp}
            maxWidth="xl"
            steps={steps}
        />
    );
}

export default MoveInscriptionModal;
